import * as React from "react";
import { useState } from "react";

import { appendCurrentLocationAsFrom, employeeProfileHref } from "@performyard/utils-isomorphic";

import { AvatarGroupModal } from "./AvatarGroupModal";

import { Link } from "../../atoms/Link";
import { Avatar, type AvatarSize } from "../Avatar";
import { Popover, PopoverContent, PopoverTrigger } from "../Popover";
import { Tooltip } from "../Tooltip";

export type AvatarGroupEmployee = {
	_id: string;
	name: string;
};

export type AvatarGroupProps = {
	/**
	 * Array of employees to display as avatars.
	 */
	employees: AvatarGroupEmployee[];
	/**
	 * Size of each avatar.
	 * @default 20
	 */
	size?: AvatarSize;
	/**
	 * Maximum number of avatars to display before showing a "+N" cap.
	 * When 0 or undefined, all avatars are shown.
	 */
	maxLength?: number;
	/**
	 * When true and no `onAvatarClick` is provided, each avatar links to the employee's profile.
	 * Ignored when `onAvatarClick` is provided.
	 * @default false
	 */
	hasLinks?: boolean;
	/**
	 * Show individual employee name tooltips on each avatar on hover.
	 * @default false
	 */
	employeeNameTooltip?: boolean;
	/**
	 * Callback fired when an individual avatar is clicked.
	 * When provided, overrides the default link-to-profile behavior.
	 */
	onAvatarClick?: (employee: AvatarGroupEmployee) => void;
	/**
	 * Controls how overflow ("+N" cap) is handled.
	 * - "modal": Clicking the +N cap opens a modal listing all employees.
	 * - "dropdown": Clicking the +N cap opens a dropdown listing overflow employees.
	 * - "none": No overflow interaction.
	 * @default "modal"
	 */
	overflowBehavior?: "modal" | "dropdown" | "none";
	/**
	 * Callback fired when an employee is selected from the overflow dropdown.
	 * Only relevant when `overflowBehavior` is "dropdown".
	 */
	onOverflowSelect?: (employee: AvatarGroupEmployee) => void;
	/**
	 * Custom title for the AvatarGroupModal when showing all members.
	 * @default "Employees"
	 */
	modalTitle?: string;
	/**
	 * Optional subtitle displayed under the modal title (e.g., workspace name).
	 */
	modalSubtitle?: string;
	/**
	 * The `_id` of the currently selected employee. When set, that avatar gets a
	 * `primary-5` selection ring. The component does not manage selection state
	 * internally — the parent controls it (typically via `onAvatarClick` / `onOverflowSelect`).
	 */
	selectedEmployeeId?: string;
	/**
	 * Optional test ID for the root element.
	 */
	"data-testid"?: string;
};

/**
 * Displays a group of overlapping avatar circles with optional overflow handling.
 *
 * Overflow is triggered only by clicking the +N cap chip, not the entire group.
 * - "modal" mode: cap opens a modal listing all employees.
 * - "dropdown" mode: cap opens a dropdown with avatar + name rows for overflow employees.
 * - Provide `onAvatarClick` for custom click behavior on individual avatars.
 */
export const AvatarGroup = ({
	employees = [],
	size = 20,
	maxLength,
	hasLinks = false,
	employeeNameTooltip = false,
	onAvatarClick,
	overflowBehavior = "modal",
	onOverflowSelect,
	modalTitle,
	modalSubtitle,
	selectedEmployeeId,
	"data-testid": testId = "avatar-group",
}: AvatarGroupProps) => {
	const [modalOpen, setModalOpen] = useState(false);

	const visibleEmployees = maxLength ? employees.slice(0, maxLength) : employees;
	const overflowEmployees = maxLength ? employees.slice(maxLength) : [];
	const hasOverflow = overflowEmployees.length > 0;
	const overflowCount = overflowEmployees.length;

	const overlapOffset = -(size / 4);

	const isModalMode = hasOverflow && overflowBehavior === "modal";
	const isDropdownMode = hasOverflow && overflowBehavior === "dropdown";

	// Cap text scales to ~50% of avatar diameter, clamped to stay legible
	const capFontSize = Math.max(Math.round(size * 0.5), 8);

	// ── Avatar rendering ──────────────────────────────────────────────────

	const wrapWithTooltip = (element: React.ReactElement, name: string, key: string) => (
		<Tooltip content={name} placement="top" key={key} asChild>
			{element}
		</Tooltip>
	);

	const renderAvatar = (employee: AvatarGroupEmployee, index: number) => {
		const marginStyle = index > 0 ? { marginLeft: overlapOffset } : undefined;
		const key = `${employee._id}-${index}`;
		const isSelected = selectedEmployeeId === employee._id;
		const ringClass = isSelected ? "ring-3 ring-primary-5" : "";

		// Custom mode: button with callback
		if (onAvatarClick) {
			const button = (
				<button
					type="button"
					className={`flex cursor-pointer rounded-full border-none bg-neutral-0 p-px ${ringClass}`}
					style={marginStyle}
					key={key}
					onClick={() => onAvatarClick(employee)}
					data-testid={`avatar-group-item-${employee._id}`}
				>
					<Avatar size={size} id={employee._id} name={employee.name} />
				</button>
			);
			return employeeNameTooltip ? wrapWithTooltip(button, employee.name, key) : button;
		}

		// Default mode with links (suppressed in modal mode — links on individual
		// avatars would conflict with the cap-only modal trigger)
		if (hasLinks && !isModalMode) {
			const link = (
				<Link
					href={appendCurrentLocationAsFrom(employeeProfileHref(employee._id))}
					forceHardReload
					underline="never"
					key={key}
				>
					<div className={`flex rounded-full bg-neutral-0 p-px ${ringClass}`} style={marginStyle}>
						<Avatar size={size} id={employee._id} name={employee.name} />
					</div>
				</Link>
			);
			return employeeNameTooltip ? wrapWithTooltip(link, employee.name, key) : link;
		}

		// Plain avatar (no link, no custom click)
		const avatar = (
			<div className={`flex rounded-full bg-neutral-0 p-px ${ringClass}`} style={marginStyle} key={key}>
				<Avatar size={size} id={employee._id} name={employee.name} />
			</div>
		);
		return employeeNameTooltip ? wrapWithTooltip(avatar, employee.name, key) : avatar;
	};

	// ── Cap chip ──────────────────────────────────────────────────────────

	const isOverflowSelected =
		selectedEmployeeId != null && overflowEmployees.some((e) => e._id === selectedEmployeeId);

	const capWrapperClass = `flex rounded-full bg-neutral-0 p-px${isOverflowSelected ? " ring-3 ring-primary-5" : ""}`;

	const capChip = (
		<div className={capWrapperClass}>
			<div
				className="flex items-center justify-center rounded-full bg-primary-5"
				style={{ width: size, height: size }}
			>
				<span className="font-semi-bold text-neutral-0" style={{ fontSize: capFontSize, lineHeight: 1 }}>
					+{overflowCount}
				</span>
			</div>
		</div>
	);

	// ── Cap rendering ─────────────────────────────────────────────────────

	const renderCap = () => {
		if (!hasOverflow) return null;

		const marginStyle = { marginLeft: overlapOffset };

		if (isDropdownMode) {
			return (
				<Popover>
					<PopoverTrigger asChild>
						<button
							type="button"
							className="flex cursor-pointer border-none bg-transparent p-0"
							style={marginStyle}
							data-testid="avatar-group-cap"
						>
							{capChip}
						</button>
					</PopoverTrigger>
					<PopoverContent align="end" sideOffset={4} className="w-auto min-w-[200px] p-0">
						{overflowEmployees.map((employee) => {
							const isRowSelected = selectedEmployeeId === employee._id;
							const rowClass = `flex w-full cursor-pointer items-center gap-1 border-none px-2 py-1 text-left text-paragraph-3 text-neutral-9 hover:bg-neutral-1${isRowSelected ? " bg-primary-1 font-semi-bold" : " bg-neutral-0"}`;
							return (
								<button
									key={employee._id}
									type="button"
									className={rowClass}
									style={{ height: 32 }}
									data-testid={`dropdown-option-${employee._id}`}
									onClick={() => onOverflowSelect?.(employee)}
								>
									<Avatar size={20} id={employee._id} name={employee.name} />
									<span className="truncate">{employee.name}</span>
								</button>
							);
						})}
					</PopoverContent>
				</Popover>
			);
		}

		if (isModalMode) {
			return (
				<Tooltip content="View All" placement="top" asChild>
					<button
						type="button"
						className="flex cursor-pointer border-none bg-transparent p-0"
						style={marginStyle}
						data-testid="avatar-group-cap"
						onClick={() => setModalOpen(true)}
					>
						{capChip}
					</button>
				</Tooltip>
			);
		}

		// "none" mode: cap is purely visual
		return (
			<div style={marginStyle} data-testid="avatar-group-cap">
				{capChip}
			</div>
		);
	};

	// ── Group assembly ────────────────────────────────────────────────────

	return (
		<>
			<div className="flex w-fit items-center" data-testid={testId}>
				{visibleEmployees.map(renderAvatar)}
				{renderCap()}
			</div>
			{isModalMode && (
				<AvatarGroupModal
					isOpen={modalOpen}
					onClose={() => setModalOpen(false)}
					employees={employees}
					title={modalTitle}
					subtitle={modalSubtitle}
					hasLinks={hasLinks}
				/>
			)}
		</>
	);
};

AvatarGroup.displayName = "AvatarGroup";
